import { Component, Inject, OnInit } from '@angular/core';
import { LogLevels } from '../../models/audit-action';
import moment from 'moment';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { concatMap, first, map } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { AuditService } from '../../services/audit.service';
import { Keepalive } from '@ng-idle/keepalive';
import { ModalService } from '../../services/modal.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'lib-auth-idle',
  templateUrl: './auth-idle.component.html',
  styleUrls: ['./auth-idle.component.css'],
  standalone: false
})
export class AuthIdleComponent implements OnInit {
  private lastActiveTime: Date = new Date();
  private isIdle: boolean = false;

  constructor(
    @Inject('env') private env: any,
    private idle: Idle,
    private authService: AuthService,
    private auditService: AuditService,
    private keepalive: Keepalive,
    private modalService: ModalService,
    private ngModalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.idle.setIdle(this.env.idleLimitSeconds);
    if (this.env.logLevel <= LogLevels.Debug) {
      this.idle.onInterrupt.subscribe(() => {
        this.lastActiveTime = new Date();
      });

      // ping interval in seconds
      this.keepalive.interval(60);

      this.keepalive.onPing.subscribe(d => {
        const diff = moment(new Date()).diff(this.lastActiveTime);
        console.log(`user has been inactive for ${moment(diff).format('mm:ss')}`);
      });
    }

    // user has n seconds to decide if they want to stay logged in
    this.idle.setTimeout(this.env.idleReloginGraceSeconds);

    // sets the default interrupts, clicks, scrolls, touches, etc
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleStart
      .pipe(
        concatMap(() => {
          return this.authService.isLoggedIn().pipe(
            map(resp => {
              return resp;
            })
          );
        })
      )
      .subscribe(loggedIn => {
        console.log('test', loggedIn);
        if (!loggedIn) {
          return;
        }

        if (this.env.logLevel <= LogLevels.Debug) {
          console.log('user is about to be logged out due to inactivity');
          this.auditService.audit('idle-warning-appeared', {
            User: this.authService.getCurrentUserName()
          });
        }

        if (!this.isIdle) {
          this.isIdle = true;
          const msg = 'Warning! Your session is about to timeout';
          this.modalService.showConfirmModal(msg, 'Log Out', 'Stay Logged In');
          this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
            if (confirm) {
              this._reset();
            } else {
              this.authService.isLoggedIn().subscribe(resp => {
                if (!resp) {
                  return;
                }

                if (this.env.logLevel <= LogLevels.Debug) {
                  this.auditService
                    .auditWithResponse('idle-warning-user-clicked-logout', {
                      User: this.authService.getCurrentUserName()
                    })
                    .subscribe(() => {
                      this.authService.signOut();
                    });
                } else {
                  this.authService.signOut();
                }

                this.idle.stop();
                this.keepalive.stop();
              });
            }
          });
        }
      });

    this.idle.onTimeout
      .pipe(
        concatMap(() => {
          return this.authService.isLoggedIn().pipe(
            map(resp => {
              return resp;
            })
          );
        })
      )
      .subscribe(loggedIn => {
        if (!loggedIn) {
          return;
        }

        if (this.env.logLevel <= LogLevels.Debug) {
          console.log('user has been logged out due to inactivity');
          this.auditService.audit('idle-timeout-auto-logout', {
            User: this.authService.getCurrentUserName()
          });
        }

        // NOTE: dismissing the modal will call
        // authService.signOut in the confirmModalCallback.
        // calling signOut directly here will trigger two calls
        // to the preSignOutCleanUp function
        this.ngModalService.dismissAll();
        this.idle.stop();
        this.keepalive.stop();
      });
    this._reset();
  }
  private _reset() {
    this.idle.watch();
    this.isIdle = false;
  }
}
